import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import GlassCard from "../components/GlassCard";
import PrimaryButton from "../components/PrimaryButton";
import { Check, Ticket } from "lucide-react";

const benefitsByRole = {
  therapist: [
    "Auto-transcribed session notes",
    "Structured clinical summaries",
    "Delivered to your inbox",
  ],
  coach: [
    "Action items captured automatically",
    "Wins, breakthroughs and next focus",
    "Notes ready before your next session",
  ],
  client: [
    "A safe space to journal between sessions",
    "Stay connected to your practitioner",
    "Notes and goals always at hand",
  ],
};

const Pricing = () => {
  const nav = useNavigate();
  const role = useMemo(() => localStorage.getItem("well_pending_role") || "therapist", []);
  const benefits = benefitsByRole[role] || benefitsByRole.therapist;

  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState(() => localStorage.getItem("well_promo_code") || "");
  const [promoNote, setPromoNote] = useState(
    localStorage.getItem("well_promo_code") ? "Promo code saved. It'll be applied after sign-in." : ""
  );

  const start = () => {
    // Persist pending promo so it can be auto-applied after Google sign-in.
    if (promoCode.trim()) localStorage.setItem("well_promo_code", promoCode.trim().toUpperCase());
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const savePromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) {
      localStorage.removeItem("well_promo_code");
      setPromoNote("");
      return;
    }
    localStorage.setItem("well_promo_code", code);
    setPromoCode(code);
    setPromoNote("Promo code saved. It'll be applied after sign-in.");
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-12 gap-8 well-page-enter">
      <Logo size="text-4xl" />
      <GlassCard className="w-full max-w-md text-center">
        <p className="text-sm font-medium tracking-wide text-[#8BA38E] uppercase">Early Access Offer</p>
        <div className="mt-4 flex items-baseline justify-center gap-2">
          <span className="text-xl text-[#8A9C91] line-through">$7.99</span>
          <span className="text-5xl font-bold text-[#2A3B32]">$4.99</span>
          <span className="text-base text-[#4A5D52]">/month</span>
        </div>
        <p className="mt-2 text-sm text-[#4A5D52]">Limited time — first 6 months</p>
        <ul className="mt-8 flex flex-col gap-4 text-left">
          {benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`benefit-${i}`}>
              <span className="mt-0.5 w-6 h-6 rounded-full bg-[#8BA38E]/20 flex items-center justify-center flex-shrink-0">
                <Check size={14} strokeWidth={2.5} className="text-[#6A7F6C]" />
              </span>
              <span className="text-base text-[#2A3B32] leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 pt-6 border-t border-white/40 text-left">
          {!promoOpen ? (
            <button
              onClick={() => setPromoOpen(true)}
              data-testid="pricing-promo-toggle"
              className="w-full inline-flex items-center justify-center gap-2 text-sm text-[#4A5D52] underline underline-offset-4 hover:text-[#2A3B32] transition-colors"
            >
              <Ticket size={14} /> Have a promo code?
            </button>
          ) : (
            <div data-testid="pricing-promo-form">
              <label className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Promo code</label>
              <div className="mt-2 flex gap-2">
                <input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onBlur={savePromo}
                  placeholder="Enter code"
                  maxLength={20}
                  data-testid="pricing-promo-input"
                  className="flex-1 h-[48px] bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] px-4 focus:outline-none focus:ring-2 focus:ring-[#8BA38E] tracking-widest font-semibold uppercase"
                />
                <button
                  onClick={savePromo}
                  data-testid="pricing-promo-save"
                  className="px-5 h-[48px] bg-[#8BA38E] hover:bg-[#7A917D] text-white rounded-[16px] font-semibold transition-colors"
                >
                  Save
                </button>
              </div>
              {promoNote && (
                <p className="mt-2 text-xs text-[#6A7F6C]" data-testid="pricing-promo-note">
                  {promoNote}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-8">
          <PrimaryButton onClick={start} data-testid="pricing-get-started-btn">
            Get Started
          </PrimaryButton>
        </div>
        <p className="mt-4 text-xs text-[#8A9C91]">Sign in with Google to continue</p>
      </GlassCard>
      <button
        onClick={() => nav("/")}
        className="text-sm text-[#4A5D52] underline underline-offset-4"
        data-testid="pricing-back-btn"
      >
        Back
      </button>
    </div>
  );
};

export default Pricing;
